/**
 * Geografija Evrope, ručno zapisana kao poligoni i linije u stepenima
 * (dužina, širina). Namerno grubo — mapa treba da se prepozna, ne da bude tačna.
 *
 * Kopno se ne crta direktno: prozor je uglavnom kopno, pa se **oduzimaju mora**
 * (Atlantik, Sredozemlje, Crno more, Baltik, Belo more), a onda se **vraćaju
 * ostrva** koja su unutar tih mora. Tako je Evropa opisana sa pet poligona
 * umesto sa jednim ogromnim izlomljenim obrisom.
 *
 * Sve se lako doteruje: dodaj tačku u niz i mapa se menja.
 */

/** Poligon kao ravan niz: [lon, lat, lon, lat, ...]. */
export type Poligon = number[]

/** Prozor mape u stepenima. */
export const OKVIR = { lonMin: -12, lonMax: 42, latMin: 34, latMax: 71 }
export const LON_SREDINA = (OKVIR.lonMin + OKVIR.lonMax) / 2
export const LAT_SREDINA = (OKVIR.latMin + OKVIR.latMax) / 2
/** Skraćenje meridijana na srednjoj širini — bez ovoga je Evropa razvučena. */
export const LON_SKRACENJE = Math.cos((LAT_SREDINA * Math.PI) / 180)

// --- mora -----------------------------------------------------------------

/** Atlantik, Severno more, Norveško i Barencovo more. */
const ATLANTIK: Poligon = [
  -16, 74, 46, 74, 46, 70.5,
  // obala Kole i severna Norveška
  41.0, 69.2, 38.0, 68.8, 35.5, 69.4, 33.0, 69.8, 31.5, 69.7, 30.0, 70.0,
  28.0, 70.9, 26.0, 71.1, 24.0, 70.9, 22.0, 70.5, 20.0, 70.1, 18.0, 69.5,
  16.0, 68.7, 14.5, 68.0, 13.0, 67.2, 12.0, 66.2, 11.0, 65.0, 9.8, 64.0,
  8.5, 63.3, 7.0, 62.8, 5.5, 62.0, 5.0, 61.0, 5.2, 60.0, 5.8, 59.0,
  6.8, 58.3, 8.5, 58.0, 10.4, 58.9, 11.2, 58.5, 11.9, 57.7,
  // Skagerak i zapadna obala Jitlanda
  10.6, 57.9, 9.0, 57.2, 8.1, 56.3, 8.2, 55.4, 8.6, 55.0, 8.9, 54.4,
  8.5, 53.9, 7.0, 53.6, 5.5, 53.4, 4.6, 52.9, 4.0, 51.9, 3.4, 51.4,
  2.5, 51.1, 1.6, 50.9, 0.2, 49.7, -1.2, 49.4, -1.9, 49.7, -2.5, 48.7,
  -4.8, 48.6, -4.4, 47.8, -2.2, 47.2, -1.2, 46.3, -1.2, 45.0, -1.4, 44.0,
  -1.8, 43.4, -3.0, 43.5, -6.0, 43.6, -8.9, 43.7, -9.3, 42.5, -9.0, 41.2,
  -9.4, 38.7, -8.9, 37.0, -7.4, 37.2, -6.3, 36.8, -6.0, 36.2, -5.6, 36.0,
  // marokanska obala do ivice prozora
  -5.9, 35.8, -6.9, 34.5, -8.5, 33.3, -9.3, 32.5, -16, 30,
]

/** Sredozemlje sa Jadranom, Egejem i Mramornim morem. */
const SREDOZEMLJE: Poligon = [
  -5.6, 35.9, -3.0, 35.5, 0.0, 35.8, 3.0, 36.8, 6.0, 37.0, 8.6, 37.1,
  11.0, 37.2, 11.0, 35.5, 10.5, 34.0, 11.0, 31.0, 33.0, 30.5, 34.5, 31.6,
  35.6, 34.6, 36.0, 35.5, 35.8, 36.5, 34.0, 36.3, 32.0, 36.3, 30.5, 36.3,
  29.0, 36.2, 28.0, 36.6, 27.3, 37.0, 26.8, 38.4, 26.2, 39.5, 26.2, 40.0,
  27.0, 40.6, 28.6, 41.1, 28.0, 41.0, 26.5, 40.7, 25.0, 40.8, 24.0, 40.6,
  23.5, 40.5, 22.5, 40.4, 23.0, 39.2, 23.5, 38.5, 24.0, 38.0, 23.6, 37.9,
  23.0, 37.4, 22.8, 36.7, 21.7, 36.9, 21.3, 38.3, 20.8, 39.0, 19.5, 40.4,
  19.0, 41.9, 18.5, 42.5, 17.0, 43.3, 15.0, 44.4, 14.0, 45.3, 13.6, 45.5,
  13.0, 45.6, 12.4, 44.5, 13.5, 43.6, 15.0, 42.0, 17.0, 41.0, 18.4, 40.1,
  17.2, 39.0, 16.5, 38.5, 15.6, 38.0, 15.9, 40.0, 14.3, 40.6, 12.3, 41.7,
  10.3, 42.9, 10.0, 44.0, 8.9, 44.4, 7.5, 43.8, 5.4, 43.2, 3.0, 43.0,
  3.2, 41.9, 0.9, 41.1, 0.0, 39.5, -0.7, 38.0, -1.9, 37.5, -3.5, 36.7,
  -5.3, 36.1,
]

/** Crno i Azovsko more. */
const CRNO_MORE: Poligon = [
  29.0, 41.2, 31.0, 41.2, 35.0, 42.0, 38.0, 41.3, 41.5, 41.6, 40.0, 43.3,
  37.8, 44.7, 36.5, 45.3, 38.0, 46.0, 39.3, 47.1, 37.0, 46.7, 35.8, 45.9,
  34.0, 44.4, 33.4, 44.5, 33.0, 45.3, 31.5, 46.5, 30.5, 46.4, 29.7, 45.5,
  28.8, 44.5, 27.9, 43.4, 27.5, 42.2, 28.2, 41.4,
]

/** Baltik sa Botnijskim, Finskim i Riškim zalivom. */
const BALTIK: Poligon = [
  10.0, 54.5, 11.5, 54.4, 12.5, 54.4, 14.0, 54.0, 16.5, 54.6, 18.6, 54.7,
  19.5, 54.5, 21.0, 55.3, 21.0, 56.3, 21.0, 57.0, 23.5, 56.9, 24.0, 58.4,
  23.0, 58.5, 24.5, 59.4, 28.0, 59.4, 30.3, 59.9, 27.0, 60.5, 24.9, 60.2,
  22.0, 60.4, 21.4, 61.6, 21.5, 63.1, 24.0, 65.3, 22.0, 65.6, 21.5, 64.0,
  19.0, 63.2, 17.5, 62.5, 17.0, 61.5, 17.5, 60.6, 18.8, 59.3, 17.0, 58.7,
  16.5, 57.5, 16.4, 56.7, 14.5, 55.4, 13.0, 55.3, 12.6, 55.6, 11.5, 55.0,
  10.5, 54.8,
]

/** Belo more — uvučeno duboko u kopno, daje severoistoku prepoznatljiv oblik. */
const BELO_MORE: Poligon = [
  33.0, 67.2, 34.5, 66.0, 36.0, 64.9, 38.5, 64.5, 40.5, 64.9, 39.8, 65.9,
  41.0, 66.6, 43.5, 66.2, 44.0, 67.6, 42.0, 68.0, 39.0, 66.6, 36.5, 66.6,
  34.5, 67.5,
]

export const MORA: Poligon[] = [ATLANTIK, SREDOZEMLJE, CRNO_MORE, BALTIK, BELO_MORE]

// --- ostrva ---------------------------------------------------------------

const VELIKA_BRITANIJA: Poligon = [
  -5.7, 50.05, -4.2, 50.3, -3.0, 50.7, -1.9, 50.7, -0.8, 50.8, 0.7, 50.8,
  1.4, 51.4, 1.7, 52.5, 0.5, 52.9, 0.2, 53.5, -0.1, 54.1, -1.2, 54.6,
  -1.5, 55.3, -2.0, 55.8, -3.1, 56.0, -2.6, 56.5, -2.1, 57.15, -3.0, 57.7,
  -4.0, 57.9, -3.0, 58.65, -4.5, 58.6, -5.0, 58.3, -5.3, 57.9, -5.8, 57.5,
  -5.7, 57.0, -5.5, 56.5, -5.8, 56.0, -5.8, 55.3, -4.8, 55.0, -3.5, 54.9,
  -3.0, 54.2, -3.1, 53.5, -4.2, 53.3, -4.8, 52.8, -4.1, 52.2, -5.2, 51.75,
  -4.2, 51.6, -3.0, 51.4, -4.2, 51.2, -5.0, 50.5,
]

const IRSKA: Poligon = [
  -10.0, 51.6, -9.0, 51.5, -8.0, 51.6, -6.4, 52.2, -6.0, 53.0, -6.2, 54.0,
  -5.5, 54.5, -5.9, 55.2, -7.0, 55.3, -8.3, 55.2, -8.8, 54.5, -9.9, 54.2,
  -9.9, 53.4, -9.0, 53.3, -9.8, 52.6, -10.4, 52.0,
]

const SICILIJA: Poligon = [12.4, 37.8, 13.5, 38.1, 15.1, 38.3, 15.6, 38.2, 15.3, 37.1, 14.5, 36.7, 12.7, 37.6]
const SARDINIJA: Poligon = [8.2, 41.1, 9.3, 41.2, 9.6, 40.5, 9.5, 39.2, 9.0, 38.9, 8.4, 39.1, 8.4, 40.5]
const KORZIKA: Poligon = [8.6, 42.7, 9.4, 42.7, 9.5, 41.9, 9.2, 41.4, 8.7, 41.6, 8.6, 42.2]
const KRIT: Poligon = [23.5, 35.25, 25.0, 35.35, 26.3, 35.3, 26.2, 34.95, 24.5, 34.85, 23.5, 35.0]
const KIPAR: Poligon = [32.3, 35.1, 34.0, 35.7, 34.6, 35.6, 33.9, 34.9, 32.9, 34.6]
const MALORKA: Poligon = [2.4, 39.7, 3.4, 39.9, 3.2, 39.3, 2.4, 39.5]
const ZELAND: Poligon = [11.0, 55.9, 12.6, 56.1, 12.6, 55.3, 11.0, 55.2]
const FIN: Poligon = [9.8, 55.6, 10.8, 55.6, 10.8, 55.0, 9.8, 55.1]
const GOTLAND: Poligon = [18.1, 57.9, 19.3, 57.6, 18.8, 56.9, 18.1, 57.2]

export const OSTRVA: Poligon[] = [
  VELIKA_BRITANIJA, IRSKA, SICILIJA, SARDINIJA, KORZIKA, KRIT, KIPAR,
  MALORKA, ZELAND, FIN, GOTLAND,
]

// --- planine --------------------------------------------------------------

export interface Venac {
  ime: string
  /** Osa venca kao lomljena linija [lon, lat, ...]. */
  osa: number[]
  /** Poluširina u stepenima. */
  sirina: number
  /** Visina grebena u svetskim jedinicama iznad okolnog terena. */
  visina: number
}

/**
 * Namerno pitomije nego na proceduralnoj mapi — Evropa treba da se čita kao
 * mreža puteva i gradova, a planine da budu prepreke, ne zid.
 */
export const VENCI: Venac[] = [
  { ime: 'Alpi', osa: [6.0, 45.2, 7.5, 45.9, 9.5, 46.5, 11.5, 47.0, 13.5, 47.2, 15.0, 47.3], sirina: 1.35, visina: 150 },
  { ime: 'Pirineji', osa: [-1.6, 43.1, 0.5, 42.7, 2.0, 42.5], sirina: 0.62, visina: 112 },
  { ime: 'Karpati', osa: [18.9, 49.4, 20.5, 49.3, 22.5, 48.2, 24.5, 47.9, 25.5, 46.5, 24.5, 45.5, 23.0, 45.3, 22.5, 44.7], sirina: 0.85, visina: 104 },
  { ime: 'Skandinavske planine', osa: [7.0, 58.8, 8.5, 60.5, 10.0, 62.0, 13.0, 64.5, 16.0, 67.0, 19.5, 68.5, 22.0, 69.4], sirina: 1.5, visina: 96 },
  { ime: 'Apenini', osa: [10.0, 44.2, 12.0, 43.3, 13.8, 42.2, 15.5, 40.8, 16.2, 39.5], sirina: 0.55, visina: 84 },
  { ime: 'Dinaridi', osa: [14.3, 45.4, 16.0, 44.2, 17.5, 43.3, 19.0, 42.5, 20.3, 41.5], sirina: 0.75, visina: 92 },
  { ime: 'Stara planina', osa: [22.5, 42.8, 24.0, 42.8, 26.0, 42.9], sirina: 0.6, visina: 78 },
  { ime: 'Rodopi', osa: [23.3, 41.6, 25.0, 41.5], sirina: 0.62, visina: 72 },
  { ime: 'Pind', osa: [21.0, 40.0, 21.8, 39.2, 22.2, 38.5], sirina: 0.55, visina: 86 },
  { ime: 'Kavkaz', osa: [39.5, 43.5, 42.5, 42.6], sirina: 0.75, visina: 155 },
  { ime: 'Sistema Central', osa: [-6.2, 40.3, -4.2, 40.8, -3.0, 41.0], sirina: 0.55, visina: 88 },
  { ime: 'Kantabrijske planine', osa: [-7.5, 43.1, -5.0, 43.0, -3.5, 43.0], sirina: 0.5, visina: 88 },
  { ime: 'Sijera Nevada', osa: [-4.2, 37.1, -2.8, 37.2], sirina: 0.5, visina: 92 },
  { ime: 'Centralni masiv', osa: [2.0, 45.0, 3.5, 45.3, 3.0, 44.3], sirina: 0.9, visina: 68 },
  { ime: 'Škotska visoravan', osa: [-5.0, 57.0, -3.6, 57.3], sirina: 0.7, visina: 58 },
  { ime: 'Šumava', osa: [12.5, 49.2, 14.5, 49.0], sirina: 0.55, visina: 54 },
  { ime: 'Sudeti', osa: [15.4, 50.7, 17.0, 50.3], sirina: 0.45, visina: 54 },
  { ime: 'Anadolska visoravan', osa: [29.0, 38.2, 32.0, 38.6, 35.0, 38.6, 38.0, 39.2], sirina: 1.55, visina: 76 },
]

/** Široke visoravni — dižu ceo kraj, bez grebena. */
export interface Visoravan {
  ime: string
  lon: number
  lat: number
  /** Poluosе u stepenima. */
  rx: number
  rz: number
  visina: number
}

export const VISORAVNI: Visoravan[] = [
  { ime: 'Iberijska meseta', lon: -4.0, lat: 40.5, rx: 3.6, rz: 2.6, visina: 52 },
  { ime: 'Masiv Šumadije i Panonije', lon: 19.5, lat: 46.5, rx: 3.0, rz: 1.8, visina: -6 },
  { ime: 'Ruska visoravan', lon: 36.0, lat: 54.0, rx: 6.0, rz: 4.0, visina: 26 },
  { ime: 'Finska jezerska ploča', lon: 27.0, lat: 62.5, rx: 4.0, rz: 3.0, visina: 22 },
]

// --- reke -----------------------------------------------------------------

export interface Reka {
  ime: string
  tok: number[]
}

export const REKE: Reka[] = [
  { ime: 'Dunav', tok: [8.2, 47.9, 10.0, 48.7, 12.1, 48.9, 13.5, 48.6, 16.4, 48.2, 17.1, 47.9, 19.0, 47.5, 19.0, 45.4, 20.5, 44.8, 22.3, 44.6, 24.0, 43.8, 26.0, 44.0, 28.0, 44.6, 29.7, 45.2] },
  { ime: 'Rajna', tok: [9.0, 46.6, 8.6, 47.6, 8.2, 49.0, 8.4, 50.0, 7.6, 50.4, 6.9, 51.2, 5.9, 51.8, 4.5, 51.9] },
  { ime: 'Rona', tok: [6.6, 46.4, 5.0, 45.8, 4.8, 44.3, 4.7, 43.7, 4.6, 43.35] },
  { ime: 'Sena', tok: [4.7, 47.5, 3.0, 48.4, 2.35, 48.85, 1.4, 49.4, 0.2, 49.5] },
  { ime: 'Loara', tok: [4.2, 44.9, 3.2, 46.3, 1.9, 47.9, 0.7, 47.4, -1.0, 47.3, -2.2, 47.2] },
  { ime: 'Laba', tok: [15.6, 50.7, 14.4, 50.2, 13.7, 51.05, 11.6, 52.1, 10.0, 53.3, 9.0, 53.9] },
  { ime: 'Visla', tok: [18.9, 49.7, 19.9, 50.05, 21.5, 51.3, 21.0, 52.25, 18.8, 53.0, 18.6, 54.4] },
  { ime: 'Odra', tok: [17.5, 49.6, 17.0, 50.7, 14.6, 52.3, 14.3, 53.0, 14.5, 53.9] },
  { ime: 'Po', tok: [7.2, 44.8, 8.5, 45.1, 10.0, 45.05, 11.5, 45.05, 12.3, 44.9] },
  { ime: 'Dnjepar', tok: [32.0, 55.3, 30.5, 53.0, 30.5, 51.5, 30.5, 50.45, 33.0, 49.0, 34.5, 48.5, 35.0, 47.9, 33.5, 46.6, 32.3, 46.5] },
  { ime: 'Don', tok: [39.0, 52.5, 41.0, 50.0, 40.0, 48.5, 39.3, 47.2] },
  { ime: 'Temza', tok: [-1.7, 51.7, -0.1, 51.5, 0.7, 51.5, 1.2, 51.5] },
  { ime: 'Tajo', tok: [-1.5, 40.3, -3.5, 39.9, -5.5, 39.7, -7.5, 39.5, -9.2, 38.7] },
  { ime: 'Ebro', tok: [-4.2, 43.0, -2.0, 42.5, -0.9, 41.65, 0.5, 41.2, 0.9, 40.7] },
  { ime: 'Volga (gornji tok)', tok: [34.0, 56.5, 37.0, 57.0, 40.0, 57.5, 43.5, 56.3] },
]

// --- gradovi --------------------------------------------------------------

export interface GradGeo {
  ime: string
  lon: number
  lat: number
}

export const GRADOVI: GradGeo[] = [
  { ime: 'Lisabon', lon: -9.14, lat: 38.72 },
  { ime: 'Madrid', lon: -3.7, lat: 40.42 },
  { ime: 'Barselona', lon: 2.17, lat: 41.39 },
  { ime: 'Pariz', lon: 2.35, lat: 48.86 },
  { ime: 'Marsej', lon: 5.37, lat: 43.3 },
  { ime: 'London', lon: -0.13, lat: 51.51 },
  { ime: 'Dablin', lon: -6.26, lat: 53.35 },
  { ime: 'Amsterdam', lon: 4.9, lat: 52.37 },
  { ime: 'Brisel', lon: 4.35, lat: 50.85 },
  { ime: 'Hamburg', lon: 9.99, lat: 53.55 },
  { ime: 'Berlin', lon: 13.4, lat: 52.52 },
  { ime: 'Minhen', lon: 11.58, lat: 48.14 },
  { ime: 'Kopenhagen', lon: 12.57, lat: 55.68 },
  { ime: 'Oslo', lon: 10.75, lat: 59.91 },
  { ime: 'Stokholm', lon: 18.07, lat: 59.33 },
  { ime: 'Helsinki', lon: 24.94, lat: 60.17 },
  { ime: 'Prag', lon: 14.42, lat: 50.08 },
  { ime: 'Beč', lon: 16.37, lat: 48.21 },
  { ime: 'Budimpešta', lon: 19.04, lat: 47.5 },
  { ime: 'Varšava', lon: 21.01, lat: 52.23 },
  { ime: 'Milano', lon: 9.19, lat: 45.46 },
  { ime: 'Rim', lon: 12.5, lat: 41.9 },
  { ime: 'Napulj', lon: 14.27, lat: 40.85 },
  { ime: 'Beograd', lon: 20.46, lat: 44.79 },
  { ime: 'Sofija', lon: 23.32, lat: 42.7 },
  { ime: 'Bukurešt', lon: 26.1, lat: 44.44 },
  { ime: 'Atina', lon: 23.73, lat: 37.98 },
  { ime: 'Istanbul', lon: 28.98, lat: 41.01 },
  { ime: 'Kijev', lon: 30.52, lat: 50.45 },
  { ime: 'Moskva', lon: 37.62, lat: 55.75 },
  { ime: 'Sankt Peterburg', lon: 30.31, lat: 59.94 },
  { ime: 'Cirih', lon: 8.54, lat: 47.37 },
]

/**
 * Mreža puteva. Nije stablo — namerno ima petlji, da mapa ima alternativne
 * pravce i uska grla, što je poenta i za kasniju igru.
 */
export const VEZE: [string, string][] = [
  ['Lisabon', 'Madrid'],
  ['Madrid', 'Barselona'],
  ['Madrid', 'Pariz'],
  ['Barselona', 'Marsej'],
  ['Marsej', 'Pariz'],
  ['Marsej', 'Milano'],
  ['Milano', 'Rim'],
  ['Rim', 'Napulj'],
  ['Milano', 'Cirih'],
  ['Cirih', 'Minhen'],
  ['Pariz', 'Brisel'],
  ['Brisel', 'Amsterdam'],
  ['Amsterdam', 'Hamburg'],
  ['Pariz', 'London'],
  ['London', 'Dablin'],
  ['Hamburg', 'Berlin'],
  ['Hamburg', 'Kopenhagen'],
  ['Kopenhagen', 'Oslo'],
  ['Oslo', 'Stokholm'],
  ['Stokholm', 'Helsinki'],
  ['Helsinki', 'Sankt Peterburg'],
  ['Sankt Peterburg', 'Moskva'],
  ['Berlin', 'Prag'],
  ['Berlin', 'Varšava'],
  ['Berlin', 'Minhen'],
  ['Prag', 'Beč'],
  ['Beč', 'Budimpešta'],
  ['Budimpešta', 'Beograd'],
  ['Beograd', 'Sofija'],
  ['Beograd', 'Bukurešt'],
  ['Sofija', 'Istanbul'],
  ['Sofija', 'Atina'],
  ['Bukurešt', 'Istanbul'],
  ['Varšava', 'Kijev'],
  ['Kijev', 'Moskva'],
  ['Beč', 'Milano'],
  ['Minhen', 'Beč'],
  ['Napulj', 'Atina'],
]

// --- geometrijski pomoćnici ------------------------------------------------

/** Da li je tačka unutar poligona (ray casting). */
export function uPoligonu(p: Poligon, x: number, y: number): boolean {
  let unutra = false
  const n = p.length / 2
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = p[i * 2]
    const yi = p[i * 2 + 1]
    const xj = p[j * 2]
    const yj = p[j * 2 + 1]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) unutra = !unutra
  }
  return unutra
}

/** Kvadrat rastojanja tačke do duži. */
export function d2DoDuzi(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  const duzina2 = dx * dx + dy * dy
  let t = duzina2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / duzina2 : 0
  t = t < 0 ? 0 : t > 1 ? 1 : t
  const cx = ax + t * dx - px
  const cy = ay + t * dy - py
  return cx * cx + cy * cy
}

/** Najmanje rastojanje do zatvorenog obrisa poligona. */
export function doObrisa(p: Poligon, x: number, y: number): number {
  let najbolje = Infinity
  const n = p.length / 2
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const d = d2DoDuzi(x, y, p[j * 2], p[j * 2 + 1], p[i * 2], p[i * 2 + 1])
    if (d < najbolje) najbolje = d
  }
  return Math.sqrt(najbolje)
}

/** Najmanje rastojanje do otvorene lomljene linije. */
export function doLinije(l: number[], x: number, y: number): number {
  let najbolje = Infinity
  const n = l.length / 2
  for (let i = 1; i < n; i++) {
    const d = d2DoDuzi(x, y, l[(i - 1) * 2], l[(i - 1) * 2 + 1], l[i * 2], l[i * 2 + 1])
    if (d < najbolje) najbolje = d
  }
  return Math.sqrt(najbolje)
}
